import React from 'react';
import './LibraryRecordCard.css';

export type LibraryRecordCardProps = {
  /** Where the card leads. A real link, so long-press, middle-click and new-tab keep working. */
  href: string;
  /** In-app navigation for a plain click or tap. */
  onOpen: () => void;
  /** Record number, such as a ticket or job code. Records without one (customers, venues) leave it out. */
  code?: React.ReactNode;
  /** Pills beside the code, or under the title when there is no code. Give each one the
   * `library-record-card__pill` class. */
  badges?: React.ReactNode;
  /** A control before the title, such as a favourite star. It sits outside the link, on the title's line. */
  leading?: React.ReactNode;
  title: React.ReactNode;
  /** Lines under the title, usually `LibraryRecordCardDetail`s. */
  details?: React.ReactNode;
  /** Short facts for the bottom row. Empty entries are skipped; the card adds the separators. */
  meta?: React.ReactNode[];
  /** Top-right control, usually a "…" menu. It sits outside the link. */
  actions?: React.ReactNode;
};

/**
 * One record in a phone library list: the whole card opens the record, an optional control
 * (a favourite star) sits before the title, and an optional actions menu sits in the top-right
 * corner. Shared by the phone libraries (Service, Installations, Inventory, Invoicing, Customers,
 * Venues, Events); it only lays out what the page passes in.
 */
export default function LibraryRecordCard({
  href,
  onOpen,
  code,
  badges,
  leading,
  title,
  details,
  meta,
  actions,
}: LibraryRecordCardProps) {
  const metaItems = (meta ?? []).filter((item) => item !== null && item !== undefined && item !== false && item !== '');
  // An empty code still keeps the header row (Inventory passes '' for an item with no code).
  const hasCode = code !== null && code !== undefined;
  // Menus close on the press outside them (MaintenanceActionsDropdown, and the older "…" menus,
  // which are plain .dropdown-menu cards); a tap that only closes an open menu must not also
  // open the card underneath.
  const pressClosedMenu = React.useRef(false);
  const handlePointerDown = () => {
    pressClosedMenu.current = Array.from(document.querySelectorAll('[role="menu"], .dropdown-menu'))
      .some((menu) => menu.getClientRects().length > 0);
  };
  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (pressClosedMenu.current && event.detail > 0) {
      pressClosedMenu.current = false;
      event.preventDefault();
      return;
    }
    // Leave modified and non-primary clicks to the browser (new tab, new window).
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    onOpen();
  };

  // The {' '} text nodes do not affect the flex layout; they keep the link's accessible
  // name readable ("DSO01530 Confirmed Microphone Addition …" rather than one run-on word).
  return (
    <li
      className="library-record-card"
      data-has-actions={actions ? 'true' : undefined}
      data-has-leading={leading ? 'true' : undefined}
    >
      {leading ? <div className="library-record-card__leading">{leading}</div> : null}
      <a className="library-record-card__link" href={href} onPointerDown={handlePointerDown} onClick={handleClick}>
        {hasCode ? (
          <>
            <span className="library-record-card__header">
              <span className="library-record-card__code">{code}</span>
              {badges ? <>{' '}<span className="library-record-card__badges">{badges}</span></> : null}
            </span>
            {' '}
          </>
        ) : null}
        <span className="library-record-card__title">{title}</span>
        {!hasCode && badges ? <>{' '}<span className="library-record-card__badges">{badges}</span></> : null}
        {details ? <>{' '}<span className="library-record-card__details">{details}</span></> : null}
        {metaItems.length > 0 ? (
          <>
            {' '}
            <span className="library-record-card__meta">
              {metaItems.map((item, index) => (
                <React.Fragment key={index}>
                  {/* A no-break space before the dot keeps it at the end of a line, never alone or leading one. */}
                  {index > 0 ? <>{'\u00a0'}<span className="library-record-card__separator" aria-hidden="true">·</span>{' '}</> : null}
                  <span className="library-record-card__meta-item">{item}</span>
                </React.Fragment>
              ))}
            </span>
          </>
        ) : null}
      </a>
      {actions ? <div className="library-record-card__actions">{actions}</div> : null}
    </li>
  );
}

/** A supporting line under the card title, with an optional leading icon. */
export function LibraryRecordCardDetail({
  icon,
  muted = false,
  children,
}: {
  icon?: React.ReactNode;
  muted?: boolean;
  children: React.ReactNode;
}) {
  return (
    <span className="library-record-card__detail" data-muted={muted ? 'true' : undefined}>
      {icon ? <span className="library-record-card__detail-icon" aria-hidden="true">{icon}</span> : null}
      <span className="library-record-card__detail-text">{children}</span>
      {' '}
    </span>
  );
}

/** The list that holds the cards. It keeps the last card clear of the chat bubble. */
export function LibraryRecordCardList({
  label,
  busy = false,
  printComplete,
  children,
}: {
  label: string;
  busy?: boolean;
  /** Mirrors the desktop table's print guard, so a partial list never prints. */
  printComplete?: boolean;
  children: React.ReactNode;
}) {
  return (
    <ul
      className="library-record-card-list"
      aria-label={label}
      aria-busy={busy || undefined}
      data-library-print-complete={printComplete}
    >
      {children}
    </ul>
  );
}

/** Placeholder card shown while the first page loads. */
export function LibraryRecordCardSkeleton() {
  return (
    <li className="library-record-card library-record-card--skeleton" aria-hidden="true">
      <span className="library-record-card__link">
        <span className="skel-bar library-record-card__skeleton-bar" data-width="short" />
        <span className="skel-bar library-record-card__skeleton-bar" data-width="long" />
        <span className="skel-bar library-record-card__skeleton-bar" data-width="medium" />
      </span>
    </li>
  );
}
